import { X, AlertTriangle } from 'lucide-react';
import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { employeeService } from '../../../services/api';

export function ProofDocumentModal({ isOpen, onClose, onUpload, onCancel, employeeData, isEsiApplicable, canDownloadPrefilledDocuments = false }) {
    const [selectedDocument, setSelectedDocument] = useState('');
    const [file, setFile] = useState(null);
    const [comment, setComment] = useState('');
    const [fileError, setFileError] = useState('');
    const [prefillLoading, setPrefillLoading] = useState(false);
    const [prefillWarningMessage, setPrefillWarningMessage] = useState('');
    const [prefillWarningDocumentName, setPrefillWarningDocumentName] = useState('');
    const [showPrefillWarningModal, setShowPrefillWarningModal] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (!isOpen) setIsSubmitting(false);
    }, [isOpen]);

    const normalizeDocumentName = (value) => {
        const v = (value || '').trim();
        if (!v) return '';
        const aliasMap = {
            'Pan & Aadhaar Linked Proof': 'PAN & Aadhaar Linked',
            'Proof of Bank Account': 'Bank Proof',
        };
        return aliasMap[v] || v;
    };

    // Get document options with conditional requirements
    const getDocumentOptions = () => {
        const baseOptions = [
            { value: 'Resume/CV', label: 'Resume/CV', mandatory: true },
            { value: 'Aadhaar', label: 'Aadhaar', mandatory: true },
            { value: 'PAN', label: 'PAN', mandatory: true },
            { value: 'PAN & Aadhaar Linked', label: 'Pan & Aadhaar Linked Proof', mandatory: true },
            { value: 'Bank Proof', label: 'Proof of Bank Account', mandatory: true },
            { value: 'Signed Appointment Letter', label: 'Signed Appointment Letter', mandatory: true },
            { value: 'Form-11 (EPF)', label: 'Form-11 (EPF)', mandatory: true, requiresPf: true },
            { value: 'Form-2 (ESI)', label: 'Form-2 (ESI)', mandatory: true, requiresEsi: true },
            { value: 'Form-F (POGA)', label: 'Form-F (POGA)', mandatory: true },
            { value: 'ESI pehchan card', label: 'ESI pehchan card', mandatory: false, requiresEsi: true, adminOnly: true }
        ];

        // Add educational certificate only if user has more than primary education
        if (!employeeData?.lessThanPrimary) {
            baseOptions.push({ 
                value: 'Educational Certificates', 
                label: 'Educational Certificates', 
                mandatory: true 
            });
        }

        // Add professional certificate only if user is not a fresher
        if (!employeeData?.isFresher) {
            baseOptions.push({ 
                value: 'Professional Certificates', 
                label: 'Professional Certificates', 
                mandatory: true 
            });
        }

        // Filter out already uploaded documents (except admin-only optional documents like ESI pehchan card)
        const uploadedDocTypes = (employeeData?.documents || []).map(doc =>
            normalizeDocumentName(doc?.documentName || doc?.document_name || doc?.type)
        );
        
        const remainingOptions = baseOptions
            .filter(option => {
                // Admin-only optional documents (like ESI pehchan card) should always be available for admin to upload/replace
                if (option.adminOnly && !option.mandatory) {
                    return true; // Always show admin-only optional documents
                }
                // Filter out already uploaded documents for other types
                return !uploadedDocTypes.includes(normalizeDocumentName(option.value));
            })
            .filter(option => {
                // Filter Form-11 (EPF) based on PF applicability
                if (option.requiresPf) {
                    return employeeData?.pfApplicable === true || employeeData?.pfApplicable === 1;
                }
                // Filter Form-2 (ESI) and ESI pehchan card based on ESI applicability
                if (option.requiresEsi) {
                    // Check both isEsiApplicable prop and employeeData.esiApplicable/persistedEsiApplicable
                    const esiApplicable = isEsiApplicable === true || 
                                         isEsiApplicable === 1 ||
                                         employeeData?.esiApplicable === true ||
                                         employeeData?.esiApplicable === 1 ||
                                         employeeData?.persistedEsiApplicable === true ||
                                         employeeData?.persistedEsiApplicable === 1;
                    return esiApplicable;
                }
                // For other options, always show
                return true;
            });

        return remainingOptions;
    };

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        setFileError('');
        
        if (selectedFile) {
            // Check file size (10MB limit)
            if (selectedFile.size > 10 * 1024 * 1024) {
                setFileError('File size should not exceed 10MB');
                setFile(null);
                return;
            }

            // Check file type - allow images and PDFs
            const allowedTypes = [
                'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/bmp', 'image/webp',
                'application/pdf'
            ];
            
            if (!allowedTypes.includes(selectedFile.type)) {
                setFileError('Only images (JPG, PNG, GIF, BMP, WebP) and PDF files are allowed');
                setFile(null);
                return;
            }

            setFile(selectedFile);
        }
    };

    const handleUpload = async () => {
        if (isSubmitting) return;
        if (!selectedDocument) {
            toast.error('Please select a document type');
            return;
        }
        
        if (!file) {
            toast.error('Please select a file');
            return;
        }

        if (fileError) {
            toast.error(fileError);
            return;
        }

        const payload = {
            documentName: selectedDocument,
            fileName: file.name,
            size: file.size,
            lastUpdated: new Date().toISOString(),
            comment,
            file: file,
        };

        setIsSubmitting(true);
        try {
            await Promise.resolve(onUpload(payload));
            setSelectedDocument('');
            setFile(null);
            setComment('');
            setFileError('');
            setPrefillWarningMessage('');
            setPrefillWarningDocumentName('');
            setShowPrefillWarningModal(false);
            onClose();
        } catch (e) {
            // Parent may toast; keep modal open for retry
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        // Reset form
        setSelectedDocument('');
        setFile(null);
        setComment('');
        setFileError('');
        setPrefillWarningMessage('');
        setPrefillWarningDocumentName('');
        setShowPrefillWarningModal(false);
        setIsSubmitting(false);
        onClose();
    };

    const formTypeByDocument = {
        'Form-11 (EPF)': 'Form-11',
        'Form-2 (ESI)': 'Form-2',
        'Form-F (POGA)': 'Form-F',
    };

    const prefilledLabelByDocument = {
        'Form-11 (EPF)': 'Form-11 (EPF)',
        'Form-2 (ESI)': 'Form-2 (ESI)',
        'Form-F (POGA)': 'Form-F (POGA)',
    };

    const handleDownloadPrefill = async () => {
        const formType = formTypeByDocument[selectedDocument];
        if (!formType || !employeeData?.id) return;
        if (!canDownloadPrefilledDocuments) {
            const selectedDocLabel = prefilledLabelByDocument[selectedDocument] || selectedDocument || 'selected document';
            setPrefillWarningMessage(
                'Fill all the details in the Previous tabs to be able to download the Pre filled documents'
            );
            setPrefillWarningDocumentName(selectedDocLabel);
            setShowPrefillWarningModal(true);
            return;
        }
        setPrefillWarningMessage('');
        setPrefillWarningDocumentName('');
        setShowPrefillWarningModal(false);
        setPrefillLoading(true);
        try {
            const { blob, filename } = await employeeService.getPrefilledTemplatePdf(employeeData.id, formType);
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            toast.success('Pre-filled template downloaded');
        } catch (err) {
            console.error('Prefill download error:', err);
            toast.error(err?.response?.data?.message || 'Failed to download pre-filled template');
        } finally {
            setPrefillLoading(false);
        }
    };

    if (!isOpen) return null;

    const documentOptions = getDocumentOptions();
    
    // Check if all documents are already uploaded
    if (documentOptions.length === 0) {
        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 w-full max-w-2xl">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-semibold">Upload Document</h3>
                        <button onClick={handleClose} className="text-gray-500 hover:text-gray-700">
                            <X size={20} />
                        </button>
                    </div>
                    <div className="text-center py-8">
                        <div className="text-6xl mb-4">✅</div>
                        <h4 className="text-lg font-medium text-gray-900 mb-2">All Required Documents Uploaded!</h4>
                        <p className="text-gray-500 mb-4">
                            You have successfully uploaded all the required documents for this employee.
                        </p>
                        <button
                            onClick={handleClose}
                            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-2xl">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold">Upload Document</h3>
                    <button onClick={handleClose} className="text-gray-500 hover:text-gray-700">
                        <X size={20} />
                    </button>
                </div>
                <div className="space-y-4">
                    <div className="relative">
                        <select
                            value={selectedDocument}
                            onChange={(e) => {
                                setSelectedDocument(e.target.value);
                                setPrefillWarningMessage('');
                                setPrefillWarningDocumentName('');
                            }}
                            className="block px-2.5 pb-2.5 pt-4 w-full text-sm text-gray-900 bg-transparent rounded-lg border border-gray-300 appearance-none focus:outline-none focus:ring-0 focus:border-blue-600 peer"
                        >
                            <option value="">Select document type</option>
                            {documentOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                        <label className="absolute text-sm text-gray-500 duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] bg-white px-2 peer-focus:px-2 peer-focus:text-blue-600 peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:top-1/2 peer-focus:top-2 peer-focus:scale-75 peer-focus:-translate-y-4 left-1">
                            Select Document
                        </label>
                    </div>
                    
                    {/* Template Download Links - Only show for specific form types */}
                    {(selectedDocument === 'Form-11 (EPF)' || selectedDocument === 'Form-2 (ESI)' || selectedDocument === 'Form-F (POGA)') && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <h4 className="text-sm font-medium text-blue-800 mb-2">📋 Download Template</h4>
                            <p className="text-xs text-blue-600 mb-3">
                                Download the pre-filled document (when all employee details are saved), then check and complete, sign, and upload.
                            </p>
                            <div className="flex flex-wrap gap-2">
                                {/* {selectedDocument === 'Form-11 (EPF)' && (
                                    <a 
                                        href="/templates/Form-11-EPF-blank.pdf" 
                                        download
                                        className="inline-flex items-center px-3 py-2 bg-blue-500 text-white text-sm rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                                    >
                                        📥 Blank Form-11 (EPF)
                                    </a>
                                )}
                                {selectedDocument === 'Form-2 (ESI)' && (
                                    <a 
                                        href="/templates/Form-2-ESI-blank.pdf" 
                                        download
                                        className="inline-flex items-center px-3 py-2 bg-blue-500 text-white text-sm rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                                    >
                                        📥 Blank Form-2 (ESI)
                                    </a>
                                )}
                                {selectedDocument === 'Form-F (POGA)' && (
                                    <a 
                                        href="/templates/Form-F-POGA-blank.pdf" 
                                        download
                                        className="inline-flex items-center px-3 py-2 bg-blue-500 text-white text-sm rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                                    >
                                        📥 Blank Form-F (POGA)
                                    </a>
                                )} */}
                                {employeeData?.id && (
                                    <button
                                        type="button"
                                        onClick={handleDownloadPrefill}
                                        disabled={prefillLoading}
                                        className="inline-flex items-center px-3 py-2 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {prefillLoading ? 'Downloading…' : '📥 Pre-filled Document'}
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    <div className="relative">
                        <input
                            type="file"
                            onChange={handleFileChange}
                            accept=".jpg,.jpeg,.png,.gif,.bmp,.webp,.pdf"
                            className="block px-2.5 pb-2.5 pt-4 w-full text-sm text-gray-900 bg-transparent rounded-lg border border-gray-300 appearance-none focus:outline-none focus:ring-0 focus:border-blue-600 peer"
                        />
                        <label className="absolute text-sm text-gray-500 duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] bg-white px-2 peer-focus:px-2 peer-focus:text-blue-600 peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:top-1/2 peer-focus:top-2 peer-focus:scale-75 peer-focus:-translate-y-4 left-1">
                            File (Images or PDF, max 10MB)
                        </label>
                        {fileError && (
                            <p className="text-red-500 text-xs mt-1">{fileError}</p>
                        )}
                        {file && (
                            <p className="text-green-600 text-xs mt-1">
                                Selected: {file.name} ({(file.size / 1024).toFixed(1)} KB)
                            </p>
                        )}
                    </div>
                    <div className="relative">
                        <textarea
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            className="block px-2.5 pb-2.5 pt-4 w-full text-sm text-gray-900 bg-transparent rounded-lg border border-gray-300 appearance-none focus:outline-none focus:ring-0 focus:border-blue-600 peer"
                            rows="3"
                        />
                        <label className="absolute text-sm text-gray-500 duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] bg-white px-2 peer-focus:px-2 peer-focus:text-blue-600 peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:top-1/2 peer-focus:top-2 peer-focus:scale-75 peer-focus:-translate-y-4 left-1">
                            Comment (Optional)
                        </label>
                    </div>
                    <div className="flex space-x-2">
                        <button
                            type="button"
                            onClick={() => handleUpload()}
                            disabled={!selectedDocument || !file || !!fileError || isSubmitting}
                            className="flex-1 bg-blue-500 text-white py-2 px-4 rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:bg-gray-300 disabled:cursor-not-allowed"
                        >
                            {isSubmitting ? 'Uploading…' : 'Upload'}
                        </button>
                        <button
                            type="button"
                            onClick={handleClose}
                            disabled={isSubmitting}
                            className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </div>

            {/* Prefilled Download Warning Modal */}
            {showPrefillWarningModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
                    <div className="bg-white rounded-lg p-6 max-w-md mx-4">
                        <div className="mb-4">
                            <div className="flex items-center gap-2 mb-2">
                                <AlertTriangle className="w-5 h-5 text-yellow-600" />
                                <h3 className="text-lg font-semibold text-yellow-600">Warning</h3>
                            </div>
                            <p className="text-sm text-gray-800">
                                {prefillWarningMessage}
                                {prefillWarningDocumentName ? (
                                    <>
                                        {' '}
                                        -{' '}
                                        <span className="font-bold text-yellow-700">
                                            {prefillWarningDocumentName}. 
                                        </span>
                                    </>
                                ) : null}
                            </p>
                        </div>
                        <div className="flex justify-end">
                            <button
                                type="button"
                                onClick={() => setShowPrefillWarningModal(false)}
                                className="px-4 py-2 text-sm font-medium text-white bg-yellow-600 hover:bg-yellow-700 rounded-md transition-colors"
                            >
                                OK
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}